import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'

import style from './GoogleWorkspaceIntegrationManageModal.style'

type GoogleWorkspaceIntegrationManageModalProps = {
  isOpen: boolean
  onClose: () => void
  integrationName: string
  workspaceDomain: string | null
  connectedAt: string | null
  lastVerifiedAt: string | null
  locale?: string
  onVerifyAgain: () => void
  onDisconnect: () => void
  disconnectPending: boolean
  disconnectError: boolean
}

const formatDate = (date: string, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date))

export default function GoogleWorkspaceIntegrationManageModal({
  isOpen,
  onClose,
  integrationName,
  workspaceDomain,
  connectedAt,
  lastVerifiedAt,
  locale = 'en-US',
  onVerifyAgain,
  onDisconnect,
  disconnectPending,
  disconnectError,
}: GoogleWorkspaceIntegrationManageModalProps): ReactElement {
  const translate = useTranslate()
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const disconnectButtonRef = useRef<HTMLButtonElement>(null)
  const confirmButtonRef = useRef<HTMLButtonElement>(null)
  const restoreDisconnectFocus = useRef(false)

  useEffect(() => {
    if (!isOpen) setConfirmDisconnect(false)
  }, [isOpen])

  useEffect(() => {
    if (confirmDisconnect) {
      confirmButtonRef.current?.focus()
    } else if (restoreDisconnectFocus.current) {
      restoreDisconnectFocus.current = false
      disconnectButtonRef.current?.focus()
    }
  }, [confirmDisconnect])

  const handleClose = () => {
    if (disconnectPending) return
    restoreDisconnectFocus.current = false
    setConfirmDisconnect(false)
    onClose()
  }

  return (
    <BaseModal
      className="GoogleWorkspaceIntegrationManageModal"
      variant="interactive"
      isOpen={isOpen}
      onClose={handleClose}
      shouldCloseOnEsc={!disconnectPending}
      shouldCloseOnOverlayClick={!disconnectPending}
      css={style}
      aria-label={translate(
        confirmDisconnect
          ? 'Disconnect ${integrationName}?'
          : 'Manage ${integrationName}',
        { integrationName },
      )}
    >
      {confirmDisconnect ? (
        <>
          <h1>
            {translate('Disconnect ${integrationName}?', { integrationName })}
          </h1>
          <p>
            {translate(
              'Disconnecting stops Extend from using this integration. To fully revoke access, also remove its API client grant in Google Admin.',
            )}
          </p>
          {disconnectError && (
            <div role="alert">
              <AlertPanel variant="severe-wcag">
                {translate(
                  '${integrationName} could not be disconnected. Try again.',
                  { integrationName },
                )}
              </AlertPanel>
            </div>
          )}
          <div className="GoogleWorkspaceIntegrationManageModal-actions">
            <Button
              variant="secondary"
              onClick={() => {
                restoreDisconnectFocus.current = true
                setConfirmDisconnect(false)
              }}
              disabled={disconnectPending}
            >
              {translate('Cancel')}
            </Button>
            <Button
              ref={confirmButtonRef}
              variant="severe"
              onClick={onDisconnect}
              loading={disconnectPending}
              disabled={disconnectPending}
            >
              {translate('Confirm Disconnect')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="GoogleWorkspaceIntegrationManageModal-header">
            <h1>
              {translate('Manage ${integrationName}', { integrationName })}
            </h1>
            <Pill color="new" size="small">
              {translate('Connected')}
            </Pill>
          </div>
          <dl>
            {workspaceDomain && (
              <div>
                <dt>{translate('Workspace domain')}</dt>
                <dd>{workspaceDomain}</dd>
              </div>
            )}
            {connectedAt && (
              <div>
                <dt>{translate('Connected on')}</dt>
                <dd>
                  <time dateTime={connectedAt}>
                    {formatDate(connectedAt, locale)}
                  </time>
                </dd>
              </div>
            )}
            {lastVerifiedAt && (
              <div>
                <dt>{translate('Last verified')}</dt>
                <dd>
                  <time dateTime={lastVerifiedAt}>
                    {formatDate(lastVerifiedAt, locale)}
                  </time>
                </dd>
              </div>
            )}
          </dl>
          <p>
            {translate(
              'Disconnecting stops Extend from using this integration. To fully revoke access, also remove its API client grant in Google Admin.',
            )}
          </p>
          <div className="GoogleWorkspaceIntegrationManageModal-actions">
            <Button variant="secondary" onClick={onVerifyAgain}>
              {translate('Verify again')}
            </Button>
            <Button
              ref={disconnectButtonRef}
              variant="severe"
              onClick={() => setConfirmDisconnect(true)}
            >
              {translate('Disconnect')}
            </Button>
          </div>
        </>
      )}
    </BaseModal>
  )
}
